import type {
  OpcUaDataKind,
  OpcUaDataValueForm,
  OpcUaFloorExpansionForm,
  OpcUaNodeIdForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";
import { messageCarriesPosition } from "../../../../../flatbuffers/types/ln2MessageOptions";
import { FLOOR_PLACEHOLDER } from "./floorRange";

/**
 * Floor templates: one authored OPC UA mapping row that LiftCloud expands into
 * one literal entry per floor when it builds a bank's CDO.
 *
 * Everything here mirrors LiftCloud's `opcua_floor_expansion.go`, so the preview
 * the operator reads and the problems the form refuses are the ones the expansion
 * would apply. The LiftCloud book chapter "OPC UA floor templates" is the
 * reference for the rules; its "Values by floor" table is replayed in
 * `__tests__/floorTemplate.test.ts`.
 *
 * Pure: no React, no I/O.
 */

/** What an expansion needs to know about the bank and the adapter it runs on. */
export interface FloorTemplateContext {
  /** Number of floors: floor `i` runs 0..span-1 and is emitted as LN2 position `i`. */
  span: number;
  /** The adapter's Position Floor Base, when the caller knows it. Non-zero wins over the template's own base. */
  adapterPositionBase?: number;
  /** The adapter's Vendor Floor Indexing (`"one_based"` implies a base of 1), when the caller knows it. */
  adapterVendorFloorIndexing?: string;
}

/** The part of a mapping row a template expands. */
interface TemplateRow {
  node: OpcUaNodeIdForm;
  nodeValue: OpcUaDataValueForm;
  floorExpansion: OpcUaFloorExpansionForm;
}

/** One floor of an expanded template. */
export interface FloorTemplateEntry {
  floor: number;
  /** The node identifier of this floor, `{floor}` substituted on the path axis. */
  identifier: string;
  /** The wire value as text; the row's own value, unchanged, when only the path varies. */
  value: string;
  /** The LN2 position the expanded entry carries: the floor itself. */
  position: number;
}

/** Inclusive range of integers a data kind can hold on the wire. */
interface KindRange {
  label: string;
  min: number;
  max: number;
}

const TWO_POW_24 = 2 ** 24;
const TWO_POW_53 = 2 ** 53;

/**
 * Kinds a data axis may use, with the range each can hold.
 *
 * Floats are limited to the integers they represent exactly (+-2^24 for a
 * 32-bit float, +-2^53 for a double), so a floor number never rounds on the
 * wire. A u64 has no upper bound here: the form holds numbers, and every one
 * that is safe to hold is far below 2^64. An i64 is bounded by what a number
 * holds exactly.
 */
const DATA_AXIS_KINDS: Partial<Record<OpcUaDataKind, KindRange>> = {
  sbyte: { label: "SByte", min: -128, max: 127 },
  byte: { label: "Byte", min: 0, max: 255 },
  u16: { label: "UInt16", min: 0, max: 65535 },
  i16: { label: "Int16", min: -32768, max: 32767 },
  u32: { label: "UInt32", min: 0, max: 4294967295 },
  i32: { label: "Int32", min: -2147483648, max: 2147483647 },
  u64: { label: "UInt64", min: 0, max: Number.MAX_SAFE_INTEGER },
  i64: { label: "Int64", min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER },
  float: { label: "Float", min: -TWO_POW_24, max: TWO_POW_24 },
  double: { label: "Double", min: -TWO_POW_53, max: TWO_POW_53 },
};

/** Text shown for the data types a value template accepts, in the order the type picker lists them. */
const DATA_AXIS_KIND_LABELS = Object.values(DATA_AXIS_KINDS)
  .map((range) => range.label)
  .join(", ");

/** True when the template varies the wire value per floor. */
function varyData(expansion: OpcUaFloorExpansionForm): boolean {
  return expansion.mode === "dataOnly" || expansion.mode === "pathAndData";
}

/** True when the template varies the node identifier per floor. */
function varyPath(expansion: OpcUaFloorExpansionForm): boolean {
  return expansion.mode === "pathOnly" || expansion.mode === "pathAndData";
}

/** Whole floors in the context; a fractional or negative span expands nothing extra. */
function floorCount(ctx: FloorTemplateContext): number {
  return Number.isFinite(ctx.span) ? Math.max(0, Math.floor(ctx.span)) : 0;
}

/**
 * The wire value of floor 0 for a data axis. Precedence is LN1's for a
 * value-encoded floor:
 *
 * 1. the adapter's Position Floor Base, when it is set (non-zero);
 * 2. the template's own `valueBase`, when it is set (non-zero);
 * 3. 1, when the adapter's Vendor Floor Indexing is `"one_based"`;
 * 4. otherwise 0.
 *
 * A base of 0 therefore means "not set" at levels 1 and 2, and a vendor whose
 * lowest floor reads 0 gets there through zero-based indexing.
 */
export function floorTemplateBase(
  expansion: OpcUaFloorExpansionForm,
  ctx: FloorTemplateContext,
): number {
  if (ctx.adapterPositionBase !== undefined && ctx.adapterPositionBase !== 0) {
    return ctx.adapterPositionBase;
  }
  if (expansion.valueBase !== 0) return expansion.valueBase;
  return ctx.adapterVendorFloorIndexing === "one_based" ? 1 : 0;
}

/**
 * Why a data axis cannot hold the floors `base .. base + span - 1`, or null when
 * both endpoints fit the row's data kind. Only the endpoints are checked: the
 * values between them are inside any range that holds both.
 */
function dataRangeProblem(
  kind: OpcUaDataKind,
  range: KindRange,
  base: number,
  span: number,
): string | null {
  const last = base + (Math.max(span, 1) - 1);
  const fits =
    Number.isInteger(base) && Number.isInteger(last) && base >= range.min && last <= range.max;
  if (fits) return null;
  const shown = span > 1 ? `${base} to ${last}` : `${base}`;
  const upper = kind === "u64" ? "no upper limit" : `${range.max}`;
  return `The floor values ${shown} do not fit ${range.label} (${range.min} to ${upper}). Pick a wider data type or a different first value.`;
}

/**
 * Why this row cannot be a floor template, or null when it can. The first
 * failing rule wins, in the order LiftCloud applies them; each message names its
 * cause so the operator knows what to change. A template that fails any of them
 * is dropped whole by LiftCloud and its floors stay unmapped.
 */
export function floorTemplateProblem(
  row: {
    msgId: string;
    active: boolean;
    node: OpcUaNodeIdForm;
    nodeValue: OpcUaDataValueForm;
    floorExpansion: OpcUaFloorExpansionForm | null;
  },
  ctx: FloorTemplateContext,
): string | null {
  const { floorExpansion: expansion } = row;
  if (expansion === null) {
    return "Choose what varies by floor: the value, the node path, or both.";
  }
  if (!messageCarriesPosition(row.msgId)) {
    return "This message carries no floor, so it cannot be expanded across floors. Only elevator Traffic and Control messages can.";
  }
  if (varyData(expansion)) {
    if (!row.active) {
      return "A value template needs an active message: an inactive row is the cleared half of an on/off pair and would be rewritten to the floor number. Use Node path for call-style nodes.";
    }
    const range = DATA_AXIS_KINDS[row.nodeValue.kind];
    if (range === undefined) {
      return `A value template needs a numeric data type (${DATA_AXIS_KIND_LABELS}); pick one in Value Type.`;
    }
    const rangeProblem = dataRangeProblem(
      row.nodeValue.kind,
      range,
      floorTemplateBase(expansion, ctx),
      floorCount(ctx),
    );
    if (rangeProblem !== null) return rangeProblem;
  }
  if (varyPath(expansion)) {
    if (row.node.identifierKind !== "string" || !row.node.identifier.includes(FLOOR_PLACEHOLDER)) {
      return `A node path template needs a String Node Identifier containing ${FLOOR_PLACEHOLDER} (for example Lift.CarCall${FLOOR_PLACEHOLDER}); each floor replaces it with its own number.`;
    }
  }
  return null;
}

/**
 * The entries a template expands to: one per floor `0 .. span-1`, the way
 * LiftCloud writes them. The wire value is `floor + base` on a data axis, and
 * every `{floor}` becomes `floor + pathBase` on a path axis. An axis that does
 * not vary leaves the node identifier or the value as authored.
 *
 * Does not validate: call `floorTemplateProblem` first.
 */
export function expandFloorTemplate(
  row: TemplateRow,
  ctx: FloorTemplateContext,
): FloorTemplateEntry[] {
  const { floorExpansion: expansion } = row;
  const base = floorTemplateBase(expansion, ctx);
  const entries: FloorTemplateEntry[] = [];
  for (let floor = 0; floor < floorCount(ctx); floor += 1) {
    entries.push({
      floor,
      identifier: varyPath(expansion)
        ? row.node.identifier.split(FLOOR_PLACEHOLDER).join(String(floor + expansion.pathBase))
        : row.node.identifier,
      value: varyData(expansion) ? String(floor + base) : row.nodeValue.value,
      position: floor,
    });
  }
  return entries;
}

/** Entries shown at each end of a long preview. */
const PREVIEW_EDGE = 3;

function entryLine(entry: FloorTemplateEntry): string {
  return `floor ${entry.floor}: ${entry.identifier} = ${entry.value} -> position ${entry.position}`;
}

/**
 * The preview of what a template expands to, as lines of text: the floor count,
 * then the first three and last three entries (all of them when there are six or
 * fewer) with an ellipsis between. `spanIsSample` labels a floor count the editor
 * invented because the adapter's own is unknown. On a data axis it says when the
 * adapter's floor numbering was not available, so the shown values use the
 * template's own base only.
 */
export function floorTemplateSummary(
  row: TemplateRow,
  ctx: FloorTemplateContext,
  spanIsSample = false,
): string {
  const entries = expandFloorTemplate(row, ctx);
  const noun = entries.length === 1 ? "floor" : "floors";
  const lines = [`${entries.length} ${noun}${spanIsSample ? " (sample)" : ""}`];
  if (entries.length <= PREVIEW_EDGE * 2) {
    lines.push(...entries.map(entryLine));
  } else {
    lines.push(
      ...entries.slice(0, PREVIEW_EDGE).map(entryLine),
      "…",
      ...entries.slice(-PREVIEW_EDGE).map(entryLine),
    );
  }
  if (
    varyData(row.floorExpansion) &&
    ctx.adapterPositionBase === undefined &&
    ctx.adapterVendorFloorIndexing === undefined
  ) {
    lines.push("Note: adapter floor numbering not applied.");
  }
  return lines.join("\n");
}
